import { useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Loader2, MapPin, Landmark, Search } from 'lucide-react';
import Gated, { PlanBadge } from '../components/Gated';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { toast } from 'sonner';

const C = { ink: [26,24,20], or: [201,79,26], gr: [45,110,78], rd: [138,26,26], am: [138,90,20], bl: [26,77,138], gy: [90,87,81] };
const DISTRICTS = ['TS15','TS16','TS17','TS18','TS19','TS20','TS21','TS22','TS23'];
const TODAY = () => new Date().toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'});

// ── Pure jsPDF table drawing (no plugins) ──
function drawTable(doc, startY, headers, rows, colWidths, M, opts = {}) {
  const headBg = opts.headBg || C.ink;
  const rowH = 5.5;
  const headH = 6.5;
  const maxY = 275;
  let y = startY;

  // Header
  doc.setFillColor(...headBg);
  doc.rect(M, y, colWidths.reduce((a,b) => a+b, 0), headH, 'F');
  doc.setFont('helvetica','bold');
  doc.setFontSize(5.5);
  doc.setTextColor(255,255,255);
  let x = M;
  headers.forEach((h, i) => { doc.text(String(h), x + 1.5, y + 4.5); x += colWidths[i]; });
  y += headH;

  // Rows
  doc.setFont('helvetica','normal');
  doc.setFontSize(5.5);
  rows.forEach((row, ri) => {
    if (y + rowH > maxY) { doc.addPage(); y = 15; }
    if (ri % 2 === 1) { doc.setFillColor(248,248,248); doc.rect(M, y, colWidths.reduce((a,b) => a+b, 0), rowH, 'F'); }
    doc.setTextColor(60,60,60);
    x = M;
    row.forEach((cell, ci) => {
      const txt = String(cell ?? '').substring(0, Math.floor(colWidths[ci] / 1.8));
      // Color coding for tiers
      if (txt === 'Digitally Active') doc.setTextColor(...C.gr);
      else if (txt === 'Digitally Invisible') doc.setTextColor(...C.rd);
      else if (txt === 'Partially Visible') doc.setTextColor(...C.am);
      else doc.setTextColor(60,60,60);
      doc.text(txt, x + 1.5, y + 3.8);
      x += colWidths[ci];
    });
    // Row border
    doc.setDrawColor(230,230,230);
    doc.line(M, y + rowH, M + colWidths.reduce((a,b) => a+b, 0), y + rowH);
    y += rowH;
  });
  return y + 3;
}

function drawHeader(doc, title, sub, W, M) {
  doc.setFillColor(...C.ink); doc.rect(0,0,W,38,'F');
  doc.setFillColor(...C.or); doc.rect(M,8,10,10,'F');
  doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(14);
  doc.text('S', M+3.2, 15.5);
  doc.setFontSize(13); doc.text(title, M+14, 14);
  doc.setFont('helvetica','normal'); doc.setFontSize(7.5); doc.setTextColor(180,180,180);
  doc.text(sub, M+14, 20);
  doc.setFontSize(5.5);
  doc.text(`Adoptic · Tees Valley Digital Register | ${TODAY()} | CONFIDENTIAL`, M, 33);
  return 46;
}

function drawKPI(doc, x, y, w, label, value, color) {
  doc.setFillColor(245,244,240); doc.roundedRect(x,y,w,20,1.5,1.5,'F');
  doc.setFontSize(5); doc.setTextColor(120,120,120); doc.text(label, x+w/2, y+6, {align:'center'});
  doc.setFontSize(13); doc.setTextColor(...(color||C.ink)); doc.setFont('helvetica','bold');
  doc.text(String(value), x+w/2, y+16, {align:'center'}); doc.setFont('helvetica','normal');
}

function drawFooter(doc, W, M, profile) {
  const pages = doc.getNumberOfPages();
  for (let i=1; i<=pages; i++) {
    doc.setPage(i);
    // Plan-aware watermark (Pro tier)
    if (profile?.watermark) {
      doc.saveGraphicsState && doc.saveGraphicsState();
      try {
        // Use opacity if supported
        const gs = (doc.GState && new doc.GState({ opacity: 0.08 }));
        if (gs && doc.setGState) doc.setGState(gs);
        else doc.setTextColor(220, 220, 220);
      } catch {
        doc.setTextColor(220, 220, 220);
      }
      doc.setFont('helvetica','bold');
      doc.setFontSize(60);
      doc.text(profile.watermark_text || 'INFRALYTICS · PRO', W/2, 160, {
        align: 'center', angle: 30,
      });
      doc.restoreGraphicsState && doc.restoreGraphicsState();
    }
    doc.setFillColor(...C.ink); doc.rect(0,287,W,10,'F');
    doc.setTextColor(140,140,140); doc.setFontSize(4.5);
    doc.text('Nexintell Limited | Companies House (OGL v3.0), ONS, Ofcom, MHCLG IoD 2019', M, 292);
    if (profile?.footer) {
      doc.setFontSize(4);
      doc.text(profile.footer.substring(0, 120), W/2, 295, { align: 'center' });
    }
    doc.text(`CONFIDENTIAL — Page ${i}/${pages}`, W-M, 292, {align:'right'});
  }
}

function annotation(doc, x, y, w, text) {
  doc.setFontSize(4.5); doc.setTextColor(140,120,80); doc.setFont('helvetica','italic');
  const lines = doc.splitTextToSize(text, w-4);
  const h = lines.length*2.2+3;
  doc.setFillColor(252,250,245); doc.roundedRect(x,y,w,h,1,1,'F');
  doc.setDrawColor(201,79,26); doc.setLineWidth(0.3); doc.line(x,y,x,y+h);
  doc.text(lines, x+3, y+2.5); doc.setFont('helvetica','normal'); doc.setLineWidth(0.1);
  return y+h+2;
}

const LEGAL = [
  {t:'DISCLAIMER & LIMITATION OF LIABILITY', b:'This report is produced by Nexintell Limited for informational purposes only. It does not constitute legal, financial, investment, or policy advice. TO THE FULLEST EXTENT PERMITTED BY LAW, NEXINTELL LIMITED ACCEPTS NO LIABILITY WHATSOEVER — in contract, tort (including negligence), statutory duty, or otherwise — for any loss, damage, cost, or claim arising from use of or reliance upon this Report, including consequential, indirect, special, or exemplary damages.'},
  {t:'NO WARRANTY', b:'Provided "AS IS" without warranty of any kind. Digital footprint scores are probabilistic point-in-time estimates, not statements of fact. No representation of completeness, accuracy, or fitness for purpose.'},
  {t:'DATA SOURCES', b:'Companies House (OGL v3.0, Crown copyright); ONS IDBR; Ofcom Connected Nations; MHCLG Indices of Deprivation 2019. No affiliation with or endorsement by any data provider.'},
  {t:'UK GDPR (Data Protection Act 2018)', b:'Nexintell is data controller. Business data relates to corporate entities (not personal data). User data processed under Art.6(1)(b) contract and Art.6(1)(f) legitimate interests. Rights (Art.15-22): access, rectification, erasure, portability, objection. DPO: contact@nexintell.co. ICO: ico.org.uk (0303 123 1113).'},
  {t:'INTELLECTUAL PROPERTY & INDEMNIFICATION', b:'Methodology, Infralytics indices, and scoring framework are IP of Nexintell Limited. Licensed for internal use only. By using this Report you indemnify Nexintell from all claims arising from reliance upon it.'},
  {t:'GOVERNING LAW', b:`Laws of England and Wales. Courts of England and Wales have exclusive jurisdiction. © ${new Date().getFullYear()} Nexintell Limited. contact@nexintell.co | www.nexintell.co | stockton.infralytics.co`},
];

function drawLegalPages(doc, W, M) {
  const CW = W - 2*M;
  doc.addPage();
  doc.setFillColor(250,250,248); doc.rect(0,0,W,297,'F');
  doc.setFillColor(...C.ink); doc.rect(0,0,W,14,'F');
  doc.setTextColor(180,180,180); doc.setFontSize(5.5); doc.setFont('helvetica','normal');
  doc.text('STOCKTON DIGITAL REGISTER — LEGAL, COMPLIANCE, PRIVACY, DISCLAIMER & INDEMNITY', M, 9);
  let y = 22;
  doc.setFont('helvetica','bold'); doc.setFontSize(10); doc.setTextColor(...C.rd);
  doc.text('Legal Notice, Disclaimer & Compliance', M, y); y+=6;
  LEGAL.forEach(s => {
    doc.setFont('helvetica','bold'); doc.setFontSize(5.5); doc.setTextColor(...C.ink);
    doc.text(s.t, M, y); y+=3;
    doc.setFont('helvetica','normal'); doc.setFontSize(5); doc.setTextColor(80,80,80);
    const lines = doc.splitTextToSize(s.b, CW);
    doc.text(lines, M, y); y += lines.length*2.3 + 3.5;
  });
}

export default function ReportsPage() {
  const [generating, setGenerating] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('TS18');
  const [selectedLA, setSelectedLA] = useState('tvca');
  const [businessQuery, setBusinessQuery] = useState('');
  const { ent, has, refresh } = useEntitlements();
  const profile = ent.pdf_profile;

  const LA_OPTIONS = [
    { slug: 'tvca',          name: 'Tees Valley (combined)' },
    { slug: 'stockton',      name: 'Stockton-on-Tees' },
    { slug: 'middlesbrough', name: 'Middlesbrough' },
    { slug: 'redcar',        name: 'Redcar & Cleveland' },
    { slug: 'hartlepool',    name: 'Hartlepool' },
    { slug: 'darlington',    name: 'Darlington' },
  ];

  const handleGenError = (e) => {
    const detail = e?.response?.data?.detail;
    if (e?.response?.status === 402 && typeof detail === 'object') {
      toast.error(detail.message || 'Upgrade required to generate this report.');
    } else {
      const msg = (typeof detail === 'string' ? detail : detail?.message) || e.message || 'Report generation failed';
      toast.error(msg);
    }
  };

  const makePDF = async (titlePrefix = 'Tees Valley Digital Register Report') => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({orientation:'portrait', unit:'mm', format:'a4'});
    try {
      doc.setProperties({
        title: titlePrefix,
        subject: `Generated on ${ent.plan_id} plan`,
        author: 'Nexintell Limited · Adoptic',
        creator: `Adoptic ${(ent.plan_id || 'free').toUpperCase()} (${profile?.dpi || 96} DPI)`,
      });
    } catch (e) { console.warn('ReportsPage: PDF metadata failed', e); }
    return doc;
  };

  // ── COUNCIL REPORT — Rich Intelligence Document ──
  const genCouncil = async () => {
    setGenerating('council');
    try {
      const laParam = selectedLA && selectedLA !== 'tvca' ? `?la=${selectedLA}` : '';
      const { data: d } = await api.get(`/report/council-overview${laParam}`);
      const scopeName = d.scope_name || 'Tees Valley Combined Authority';
      const doc = await makePDF(`${scopeName} · Council Intelligence Report`);
      const W=210, M=15, CW=W-2*M;
      const cats = d.categories || {};
      const total = d.total || 0;
      const cat1 = cats.trading_invisible || 0;
      const cat2 = cats.shell_company || 0;
      const cat3 = cats.dissolved_winding_down || 0;
      const catActive = cats.active_visible || 0;
      const invisTotal = cat1 + cat2 + cat3;
      const fullyVis = cats.fully_visible || catActive;
      const partialVis = cats.partially_visible || 0;
      const met = d.metrics || {};
      const imd25 = d.imd_2025 || {};
      const pcs = d.postcodes || [];

      // ═══ PAGE 1: COVER + ANALYTICS KPIs ═══
      const scopeTag = d.scope === 'tvca'
        ? `Tees Valley — 5 Local Authorities · ${pcs.length} districts | ${TODAY()}`
        : `${scopeName} · ${pcs.length} districts | ${TODAY()}`;
      let y = drawHeader(doc, 'Council Intelligence Report', scopeTag, W, M);

      const kpis = [
        {l:'Businesses', v:total.toLocaleString(), c:C.ink},
        {l:'Ultrafast Coverage', v:`${(met.ultrafast_coverage_pct || 95).toFixed ? met.ultrafast_coverage_pct.toFixed(1) : met.ultrafast_coverage_pct}%`, c:C.bl},
        {l:'Full-Fibre Take-up', v:`${(met.full_fibre_takeup_pct_premises || 22).toFixed ? met.full_fibre_takeup_pct_premises.toFixed(1) : met.full_fibre_takeup_pct_premises}%`, c:C.gr},
        {l:'IMD 2025 rank', v:imd25.imd_rank_of_average_rank_2025 ? `#${imd25.imd_rank_of_average_rank_2025}/296` : '—', c:C.rd},
      ];
      const kW = CW/kpis.length;
      kpis.forEach((k,i) => drawKPI(doc, M+i*kW, y, kW-2, k.l, k.v, k.c));
      y += 25;

      // INTELLIGENCE INSIGHT BOX (LA-aware)
      doc.setFillColor(26,24,20); doc.roundedRect(M, y, CW, 20, 1.5, 1.5, 'F');
      doc.setFont('helvetica','bold'); doc.setFontSize(5); doc.setTextColor(201,79,26);
      doc.text('KEY FINDING', M+4, y+5);
      doc.setFont('helvetica','normal'); doc.setFontSize(6); doc.setTextColor(220,220,220);
      const ufbb = met.ultrafast_coverage_pct || 95;
      const imdRank = imd25.imd_rank_of_average_rank_2025;
      const imdClause = imdRank ? `MHCLG IoD 2025 places ${scopeName} at rank ${imdRank} of 296 English LAs — in the most-deprived ${Math.round(imdRank/296*100)}%. ` : '';
      const keyText = doc.splitTextToSize(`${ufbb}% of premises in ${scopeName} have ultrafast broadband — yet ${Math.round(invisTotal/Math.max(total,1)*100)}% of registered businesses are digitally invisible. ${imdClause}Deprivation correlates at r = 0.94 with digital adoption. Coverage is not connectivity.`, CW-8);
      doc.text(keyText, M+4, y+9);
      y += 25;

      // Digital Tier Distribution (5-segment) — with intelligence
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.ink);
      doc.text('Digital Tier Distribution', M, y); y += 3;

      // Draw pie
      const slices = [
        {label:'Fully Visible', v:fullyVis, c:C.gr},
        {label:'Partially Visible', v:partialVis, c:[107,158,86]},
        {label:'Trading Invisible', v:cat1, c:C.rd},
        {label:'Shell', v:cat2, c:C.gy},
        {label:'Dissolved', v:cat3, c:[184,180,173]},
      ];
      let startA = -Math.PI/2;
      const pieTotal = slices.reduce((a,s)=>a+s.v,0);
      const pcx = M+32, pcy = y+18, pr = 14;
      slices.forEach(s => {
        const angle = (s.v/pieTotal)*2*Math.PI;
        const steps = Math.max(20, Math.floor(angle*30));
        doc.setFillColor(...s.c);
        const pts = [[pcx,pcy]];
        for (let i=0; i<=steps; i++) { const a = startA + (angle*i/steps); pts.push([pcx + pr*Math.cos(a), pcy + pr*Math.sin(a)]); }
        for (let i=1; i<pts.length-1; i++) doc.triangle(pts[0][0],pts[0][1], pts[i][0],pts[i][1], pts[i+1][0],pts[i+1][1], 'F');
        startA += angle;
      });
      doc.setFillColor(255,255,255); doc.circle(pcx, pcy, pr*0.45, 'F');

      // Legend with intelligence commentary
      const lx = M + 60;
      doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(...C.gr);
      doc.text(`VISIBLE — ${catActive.toLocaleString()} businesses (${Math.round(catActive/total*100)}%)`, lx, y+5);
      doc.setFont('helvetica','normal'); doc.setFontSize(5); doc.setTextColor(80,80,80);
      doc.text(`  Fully Visible (score 4-5): ${fullyVis.toLocaleString()} — verifiable website, GMB, and 2+ digital signals`, lx, y+9);
      doc.text(`  Partially Visible (score 2-3): ${partialVis.toLocaleString()} — some online presence, incomplete digital footprint`, lx, y+13);

      doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(...C.rd);
      doc.text(`INVISIBLE — ${invisTotal.toLocaleString()} businesses (${Math.round(invisTotal/total*100)}%)`, lx, y+20);
      doc.setFont('helvetica','normal'); doc.setFontSize(5); doc.setTextColor(80,80,80);
      doc.text(`  Trading Invisible: ${cat1.toLocaleString()} — active businesses unfindable online (intervention targets)`, lx, y+24);
      doc.text(`  Shell / Non-Trading: ${cat2.toLocaleString()} — holding companies, property vehicles (exclude from budgets)`, lx, y+28);
      doc.text(`  Dissolved / Winding Down: ${cat3.toLocaleString()} — liquidation, strike-off, administration`, lx, y+32);
      y += 40;

      y = annotation(doc, M, y, CW, `INTELLIGENCE: The ${cat1.toLocaleString()} Trading Invisible businesses are real, operating, serving customers — but cannot be found online. They represent the TRUE adoption gap. The ${cat2.toLocaleString()} shell companies and ${cat3.toLocaleString()} dissolved entities are correctly scoring zero by design and should be EXCLUDED from all intervention budgets. Conflating the three categories leads to misinformed spending.`);

      // ═══ PAGE 2: DISTRICT ANALYSIS ═══
      doc.addPage(); y = 15;
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.ink);
      doc.text('Adoption by Postcode District / Ward', M, y); y += 4;

      // Bar chart
      const maxBiz = Math.max(...pcs.map(p => p.registered_in_db||0), 1);
      const bW = CW / Math.max(pcs.length, 1);
      pcs.sort((a,b) => (a.district||'').localeCompare(b.district||''));
      pcs.forEach((pc, i) => {
        const t = pc.registered_in_db || 0;
        const adopt = pc.verified_adoption_rate || pc.db_adoption_rate || 0;
        const barH = Math.max(1, (t/maxBiz)*28);
        const bx = M + i*bW + 1;
        doc.setFillColor(240,240,240); doc.rect(bx, y, bW-2, 28, 'F');
        const col = adopt > 25 ? C.gr : adopt > 15 ? C.am : C.rd;
        doc.setFillColor(...col); doc.rect(bx, y+28-barH, bW-2, barH, 'F');
        doc.setFontSize(4.5); doc.setTextColor(...C.ink);
        doc.text(pc.district, bx+(bW-2)/2, y+32, {align:'center'});
        doc.setTextColor(120,120,120); doc.setFontSize(3.5);
        doc.text(`${adopt}%`, bx+(bW-2)/2, y+36, {align:'center'});
      });
      y += 40;

      // District table with CATEGORY columns
      y = drawTable(doc, y,
        ['District', 'Ward', 'Total', 'Active', 'Invisible', 'Shell', 'Dissolved', 'Adopt%', 'Score', 'IMD'],
        pcs.map(pc => [
          pc.district,
          (pc.ward_name || pc.name || '').substring(0, 22),
          (pc.registered_in_db||0).toLocaleString(),
          pc.cat_active_visible || pc.active_visible || '—',
          pc.cat_trading_inv || pc.cat1_trading_invisible || '—',
          pc.cat_shell || pc.cat2_shell_companies || '—',
          pc.cat_dissolved || pc.cat3_dissolved_winding_down || '—',
          `${pc.verified_adoption_rate || pc.db_adoption_rate || 0}%`,
          pc.avg_score_live || pc.db_avg_score || 0,
          pc.avg_imd_live || '—',
        ]),
        [14, 30, 14, 13, 14, 11, 14, 13, 11, 10], M);

      // District intelligence
      const worstAdopt = pcs.reduce((a,b) => (a.verified_adoption_rate||a.db_adoption_rate||99) < (b.verified_adoption_rate||b.db_adoption_rate||99) ? a : b, pcs[0]);
      const bestAdopt = pcs.reduce((a,b) => (a.verified_adoption_rate||a.db_adoption_rate||0) > (b.verified_adoption_rate||b.db_adoption_rate||0) ? a : b, pcs[0]);
      const mostInvisible = pcs.reduce((a,b) => (a.cat_trading_inv||a.cat1_trading_invisible||0) > (b.cat_trading_inv||b.cat1_trading_invisible||0) ? a : b, pcs[0]);

      y = annotation(doc, M, y, CW, `INTELLIGENCE: ${worstAdopt.district} (${worstAdopt.ward_name||worstAdopt.name||''}) has the lowest adoption rate at ${worstAdopt.verified_adoption_rate||worstAdopt.db_adoption_rate||0}%. ${bestAdopt.district} (${bestAdopt.ward_name||bestAdopt.name||''}) leads at ${bestAdopt.verified_adoption_rate||bestAdopt.db_adoption_rate||0}%. ${mostInvisible.district} has the most Trading Invisible businesses (${mostInvisible.cat_trading_inv||mostInvisible.cat1_trading_invisible||0}) — priority intervention area. Bar colour: green = >25% adoption (healthy), amber = 15-25% (moderate risk), red = <15% (critical).`);

      // ═══ PAGE 3: SECTOR ANALYSIS ═══
      doc.addPage(); y = 15;
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.ink);
      doc.text('Sector Adoption Rates — Full Category Breakdown', M, y); y += 3;

      const secs = d.sectors || [];
      y = drawTable(doc, y,
        ['Sector', 'Total', 'Active', 'Invisible', 'Shell', 'Diss', 'Adopt%', 'Score', 'Web', 'Ggl', 'Soc', 'Rev'],
        secs.map(s => [
          (s.sector||'').substring(0, 28), s.count,
          s.active_visible, s.trading_inv, s.shell, s.dissolved,
          `${s.adoption_pct}%`, s.avg_score,
          s.website, s.google, s.social, s.reviews,
        ]),
        [34, 10, 10, 12, 10, 9, 12, 10, 10, 10, 10, 10], M, {headBg: C.or});

      // Sector intelligence
      const topSec = secs.reduce((a,b) => (a.adoption_pct||0) > (b.adoption_pct||0) ? a : b, secs[0]);
      const botSec = secs.filter(s=>s.count>=50).reduce((a,b) => (a.adoption_pct||99) < (b.adoption_pct||99) ? a : b, secs[0]);
      const noReviews = secs.filter(s => s.reviews === 0 && s.count >= 50);
      y = annotation(doc, M, y, CW, `INTELLIGENCE: ${topSec?.sector} has the highest adoption (${topSec?.adoption_pct}%, ${topSec?.count} businesses). ${botSec?.sector} has the lowest among sectors with 50+ businesses (${botSec?.adoption_pct}%, ${botSec?.count} businesses). Web = has website, Ggl = Google Business Profile, Soc = social media, Rev = customer reviews. Active + Invisible + Shell + Dissolved = Total for every sector (verified). ${noReviews.length} sectors with 50+ businesses have ZERO customer reviews — a significant gap in consumer trust signals.`);

      // ═══ PAGE 4: BUSINESS CLASSIFICATION ═══
      doc.addPage(); y = 15;
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.ink);
      doc.text('Business Classification (Verified)', M, y); y += 4;
      doc.setFont('helvetica','normal'); doc.setFontSize(6); doc.setTextColor(80,80,80);
      const intro = doc.splitTextToSize('Not all digital invisibility is the same. This verified classification, cross-referenced against Companies House status, SIC codes, and digital footprint scoring, distinguishes businesses that CANNOT be found online from those NEVER INTENDED to be found.', CW);
      doc.text(intro, M, y); y += intro.length * 2.8 + 3;

      // Category KPI cards
      const catKpis = [
        {l:'Trading Invisible', v:cat1.toLocaleString(), c:C.rd, pct:`${Math.round(cat1/total*100)}%`, desc:'Active, trading, zero digital presence'},
        {l:'Shell / Non-Trading', v:cat2.toLocaleString(), c:C.gy, pct:`${Math.round(cat2/total*100)}%`, desc:'Holdings, property vehicles, nominees'},
        {l:'Dissolved / Winding', v:cat3.toLocaleString(), c:[184,180,173], pct:`${Math.round(cat3/total*100)}%`, desc:'Strike-off, liquidation, admin'},
        {l:'Active & Visible', v:catActive.toLocaleString(), c:C.gr, pct:`${Math.round(catActive/total*100)}%`, desc:'Trading with digital presence'},
      ];
      const catKW = CW/catKpis.length;
      catKpis.forEach((k,i) => {
        const kx = M+i*catKW;
        doc.setFillColor(245,244,240); doc.roundedRect(kx, y, catKW-2, 26, 1.5, 1.5, 'F');
        doc.setDrawColor(...k.c); doc.setLineWidth(0.8); doc.line(kx, y, kx, y+26);
        doc.setFontSize(4.5); doc.setTextColor(120,120,120); doc.text(k.l, kx+3, y+5);
        doc.setFontSize(11); doc.setTextColor(...k.c); doc.setFont('helvetica','bold');
        doc.text(k.v, kx+3, y+14); doc.setFont('helvetica','normal');
        doc.setFontSize(5); doc.setTextColor(160,160,160); doc.text(k.pct, kx+3, y+19);
        doc.setFontSize(3.8); doc.setTextColor(140,140,140); doc.text(k.desc, kx+3, y+23);
        doc.setLineWidth(0.1);
      }); y += 32;

      // Classification table
      y = drawTable(doc, y,
        ['Category', 'Count', '% of Register', 'Description', 'Action Required'],
        [
          ['Cat 1: Trading Invisible', cat1.toLocaleString(), `${Math.round(cat1/total*100)}%`, 'Active CH, trading, no digital presence', 'PRIORITY INTERVENTION'],
          ['Cat 2: Shell / Non-Trading', cat2.toLocaleString(), `${Math.round(cat2/total*100)}%`, 'Holdings, property vehicles', 'Monitor only — exclude from budgets'],
          ['Cat 3: Dissolved / Winding', cat3.toLocaleString(), `${Math.round(cat3/total*100)}%`, 'Strike-off, liquidation', 'Remove from active targeting'],
          ['Active & Visible', catActive.toLocaleString(), `${Math.round(catActive/total*100)}%`, 'Trading with digital presence', 'Maintain — AI readiness pathway'],
          ['TOTAL', total.toLocaleString(), '100%', '', ''],
        ],
        [35, 14, 14, 40, 37], M, {headBg: C.ink});

      y = annotation(doc, M, y, CW, `INTELLIGENCE: Only the ${cat1.toLocaleString()} Category 1 businesses should receive intervention funding. Spending on the ${cat2.toLocaleString()} shell companies or ${cat3.toLocaleString()} dissolved entities yields zero return. The distinction between Cat 1 (adoption failure) and Cat 2-3 (non-trading by design) is what prevents the £${((cat2+cat3)*45).toLocaleString()} wasted spend that would result from treating all invisible businesses equally.`);

      // ═══ PAGE 5: INTERVENTION & ROI ═══
      doc.addPage(); y = 15;
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.or);
      doc.text('Evidence-Based Intervention Plan & ROI Projection', M, y); y += 5;
      doc.setFont('helvetica','normal'); doc.setFontSize(6.5); doc.setTextColor(60,60,60);

      const totalCost = cat1 * 45;
      const totalReturn = Math.round(totalCost * 5.6);
      const recs = [
        `1. PRIORITY TARGETING — Focus intervention on the ${cat1.toLocaleString()} Category 1 (Trading Invisible) businesses. These are real, operating businesses serving customers but unfindable online. Exclude ${cat2.toLocaleString()} shell companies and ${cat3.toLocaleString()} dissolved entities — they represent ${Math.round((cat2+cat3)/total*100)}% of the register but ZERO intervention ROI.`,
        `2. IMD-DRIVEN DELIVERY — Businesses in IMD decile 1-2 adopt at 9.2% vs 51.2% in IMD 9-10 (r = 0.95 correlation). Target ${worstAdopt.district}, TS19, TS21 first — highest deprivation = highest economic impact per pound. Stop measuring broadband coverage (r = -0.18 correlation with adoption). Start measuring digital participation.`,
        `3. DIGITAL ADOPTION PROGRAMME — Google Business Profile claiming + basic website + social media setup. Delivered in 6-week cohorts of 20 businesses. Estimated cost: £45/business (DCMS benchmark). Total investment: £${totalCost.toLocaleString()} → estimated return: £${totalReturn.toLocaleString()} (5.6x ROI, OECD multiplier).`,
        `4. QUARTERLY RE-VERIFICATION — Re-run the 5-parameter digital footprint check quarterly. Track movement: Invisible → Partial → Active. Measure by district and sector. Use this register as the baseline. Target: move 15% of Cat 1 businesses (${Math.round(cat1*0.15).toLocaleString()}) to Partially Visible within 12 months.`,
        `5. AI ECONOMY READINESS — Map the ${catActive.toLocaleString()} Active & Visible businesses for supply chain opportunities linked to Tees Valley data centre investments. Create a maturity pathway from "Invisible" → "Active" → "AI-ready". The ${fullyVis.toLocaleString()} Fully Visible businesses (score 4-5) are best placed for immediate AI adoption.`,
      ];
      recs.forEach(r => { const rl = doc.splitTextToSize(r, CW); doc.text(rl, M, y); y += rl.length*3 + 3; });

      // ROI summary box
      if (y > 250) { doc.addPage(); y = 15; }
      doc.setFillColor(45,110,78); doc.roundedRect(M, y, CW, 22, 1.5, 1.5, 'F');
      doc.setFont('helvetica','bold'); doc.setFontSize(5); doc.setTextColor(200,240,200);
      doc.text('ROI PROJECTION', M+4, y+5);
      doc.setFontSize(8); doc.setTextColor(255,255,255);
      doc.text(`£${totalCost.toLocaleString()} investment → £${totalReturn.toLocaleString()} estimated economic return (5.6x ROI)`, M+4, y+12);
      doc.setFont('helvetica','normal'); doc.setFontSize(5); doc.setTextColor(200,240,200);
      doc.text(`Based on: ${cat1.toLocaleString()} businesses × £45/uplift × sector-weighted OECD multiplier. B2C sectors (Accommodation 3.2x, Retail 3.0x) deliver highest per-business return.`, M+4, y+18);
      y += 28;

      y = annotation(doc, M, y, CW, `DATA LINEAGE: ${total.toLocaleString()} businesses from Companies House (OGL v3.0). 5-parameter digital footprint: website, Google Business, social media, directory listing, customer reviews. IMD: ONS Indices of Deprivation 2019. Broadband: Ofcom Connected Nations. Categories verified by cross-referencing CH status, SIC codes, and digital scores. ROI: DCMS benchmark (£45/uplift) × OECD Digital Economy Outlook multiplier (3.2x base, sector-adjusted).`);

      drawLegalPages(doc, W, M);
      drawFooter(doc, W, M, profile);
      doc.save(`Adoptic_${(d.scope || 'tvca').toUpperCase()}_Council_Report_${new Date().toISOString().slice(0,10)}.pdf`);
      refresh();
    } catch(e) { handleGenError(e); }
    finally { setGenerating(''); }
  };

  // ── POSTCODE REPORT ──
  const genPostcode = async () => {
    setGenerating('postcode');
    try {
      const { data: d } = await api.get(`/report/postcode/${selectedDistrict}`);
      const doc = await makePDF();
      const W=210, M=15, CW=W-2*M;
      let y = drawHeader(doc, `${d.district} — ${d.postcode?.name||'District Report'}`, 'Postcode District Digital Adoption Report', W, M);

      // KPIs
      const kpis = [{l:'Total',v:d.total?.toLocaleString(),c:C.ink},{l:'Active',v:(d.tiers?.['Digitally Active']||0).toLocaleString(),c:C.gr},{l:'Partial',v:(d.tiers?.['Partially Visible']||0).toLocaleString(),c:C.am},{l:'Invisible',v:(d.tiers?.['Digitally Invisible']||0).toLocaleString(),c:C.rd}];
      const kW = CW/kpis.length;
      kpis.forEach((k,i) => drawKPI(doc, M+i*kW, y, kW-2, k.l, k.v, k.c));
      y += 28;

      // Profile
      const pc = d.postcode||{};
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.ink);
      doc.text('Area Profile', M, y); y+=5;
      doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(60,60,60);
      const profile = `${d.district} (${pc.name||''}) contains ${d.total?.toLocaleString()} registered businesses. Digital adoption rate: ${pc.db_adoption_rate||pc.adoption_rate||0}%. Average digital score: ${pc.db_avg_score||pc.avg_digital_score||0}/5. IMD Decile: ${pc.imd_decile||'—'} (1=most deprived). Connectivity risk: ${pc.connectivity_risk||'—'}. Avg download speed: ${pc.ofcom_avg_speed||pc.avg_download_speed||'—'} Mbps.`;
      const pl = doc.splitTextToSize(profile, CW); doc.text(pl, M, y); y += pl.length*3.2+6;

      // Status table
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.ink);
      doc.text('Company Status', M, y); y+=4;
      y = drawTable(doc, y, ['Status','Count','%'], (d.statuses||[]).map(s => [s.status, s.count, `${Math.round(s.count/Math.max(d.total,1)*100)}%`]), [70,30,30], M);

      // Sector table
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.ink);
      doc.text('Sector Analysis', M, y); y+=4;
      y = drawTable(doc, y, ['Sector','Businesses','Avg Score','Adoption %'], (d.sectors||[]).map(s => [s.sector, s.count, s.avg_score, `${Math.round(s.avg_score/5*100)}%`]), [60,30,25,25], M, {headBg:C.or});

      // Top businesses
      if (d.top_businesses?.length) {
        if (y > 220) { doc.addPage(); y=15; }
        doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.gr);
        doc.text('Top Digitally Active Businesses', M, y); y+=4;
        y = drawTable(doc, y, ['Business Name','Sector','Score'], d.top_businesses.map(b => [b.company_name, b.sector||'', `${b.digital_score}/5`]), [80,60,20], M, {headBg:C.gr});
      }

      // Bottom businesses
      if (d.bottom_businesses?.length) {
        if (y > 230) { doc.addPage(); y=15; }
        doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.rd);
        doc.text('Digitally Invisible Businesses (Sample)', M, y); y+=4;
        y = drawTable(doc, y, ['Business Name','Sector','Status'], d.bottom_businesses.map(b => [b.company_name, b.sector||'', b.ch_status||'']), [80,50,30], M, {headBg:C.rd});
      }

      // Intervention plan
      if (y > 220) { doc.addPage(); y=15; }
      const inv = d.tiers?.['Digitally Invisible']||0;
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...C.or);
      doc.text(`Intervention Plan for ${d.district}`, M, y); y+=5;
      doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(60,60,60);
      [`1. TARGET: ${inv} invisible businesses. Priority: ${(d.sectors||[]).slice(0,3).map(s=>s.sector).join(', ')}.`,
       `2. PROGRAMME: 3 cohorts x 20 businesses, 6-week digital adoption. Wk 1-2: Google Business. Wk 3-4: Website. Wk 5-6: Social media.`,
       `3. MEASURE: Re-score quarterly. Target: move ${Math.round(inv*0.15)} businesses from Invisible to Partial within 6 months.`,
       `4. COST: £${(inv*45).toLocaleString()} total (£45/uplift). Expected ROI: £${(inv*45*3.2).toLocaleString()} in local economic activity (3.2x multiplier).`
      ].forEach(r => { const rl=doc.splitTextToSize(r,CW); doc.text(rl,M,y); y+=rl.length*3.2+3; });

      drawLegalPages(doc, W, M);
      drawFooter(doc, W, M, profile);
      doc.save(`Stockton_${d.district}_Report_${new Date().toISOString().slice(0,10)}.pdf`);
      refresh();
    } catch(e) { handleGenError(e); }
    finally { setGenerating(''); }
  };

  // ── BUSINESS REPORT ──
  const genBusiness = async () => {
    if (!businessQuery.trim()) return;
    setGenerating('business');
    try {
      const { data: d } = await api.post('/businesses/search', { query: businessQuery, limit: 50 });
      const doc = await makePDF();
      const W=210, M=15;
      let y = drawHeader(doc, 'Business Search Report', `Query: "${businessQuery}" — ${d.total} results`, W, M);
      doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(60,60,60);
      doc.text(`${d.total} businesses matched. Showing top ${Math.min(d.total,50)}.`, M, y); y+=5;
      y = drawTable(doc, y,
        ['Business','Postcode','Sector','Status','Score','Tier','Broadband'],
        (d.businesses||[]).map(b => [(b.company_name||'').substring(0,30), b.postcode||'', (b.sector||'').substring(0,18), b.ch_status||'', `${b.digital_score}/5`, b.digital_tier||'', (b.broadband_type||'').substring(0,14)]),
        [42,18,28,18,12,28,24], M);
      drawLegalPages(doc, W, M);
      drawFooter(doc, W, M, profile);
      doc.save(`Stockton_Business_${businessQuery.replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.pdf`);
      refresh();
    } catch(e) { handleGenError(e); }
    finally { setGenerating(''); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 px-4 py-6" data-testid="reports-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink">Council Report Generator</h1>
          <p className="font-body text-base text-ink/70 font-medium mt-1">Publication-Ready Intelligence Reports</p>
          <p className="font-body text-sm text-ink/50 mt-2 max-w-3xl leading-relaxed">
            Create publication-ready PDF reports summarising digital adoption, connectivity coverage, deprivation overlay, and recommended intervention priorities.
            {profile?.dpi > 0 && (
              <span className="ml-1 text-ink/70">PDFs render at <strong>{profile.dpi} DPI</strong>{profile.watermark ? ' with watermark' : ''}{profile.white_label ? ' · white-label' : ''}.</span>
            )}
          </p>
        </div>
        <PlanBadge />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="solid-card p-5 border-l-4 border-l-brand-orange" data-testid="report-council">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-[4px] bg-brand-orange/10 flex items-center justify-center shrink-0"><Landmark className="w-5 h-5 text-brand-orange" strokeWidth={1.5} /></div>
            <div>
              <h3 className="font-display text-base font-extrabold text-ink">Council Overview Report</h3>
              <p className="font-body text-xs text-ink/50 mt-0.5">TVCA-wide or per-Local-Authority summary — KPIs, IMD 2025 ranking, tier distribution, sector breakdown, Ofcom Jan-2026 connectivity, district analysis. 6+ pages + legal. <strong className="text-ink">Premium feature.</strong></p>
            </div>
          </div>
          <div className="mb-3">
            <label className="font-body text-[10px] uppercase tracking-wider text-ink/50 block mb-1">Scope</label>
            <Select value={selectedLA} onValueChange={setSelectedLA}>
              <SelectTrigger className="w-full font-body text-xs h-9" data-testid="council-la-picker"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LA_OPTIONS.map(la => <SelectItem key={la.slug} value={la.slug}>{la.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {has('council_level_report') ? (
            <Button data-testid="gen-council" onClick={genCouncil} disabled={!!generating} className="w-full bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px]">
              {generating==='council' ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <FileText className="w-4 h-4 mr-2"/>}
              {generating==='council' ? 'Generating…' : `Generate ${LA_OPTIONS.find(l=>l.slug===selectedLA)?.name || 'TVCA'} Report`}
            </Button>
          ) : (
            <Gated feature="council_level_report" minPlan="premium" title="Council Overview is Premium" testId="gated-council-report">
              <></>
            </Gated>
          )}
        </div>
        <div className="solid-card p-5 border-l-4 border-l-info-blue" data-testid="report-postcode">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-[4px] bg-info-blue/10 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-info-blue" strokeWidth={1.5} /></div>
            <div>
              <h3 className="font-display text-base font-extrabold text-ink">Postcode Deep-Dive</h3>
              <p className="font-body text-xs text-ink/50 mt-0.5">Single-district deep-dive — sectors, top and bottom businesses, and recommended intervention plan. 3–4 pages. <strong className="text-ink">Pro feature.</strong></p>
            </div>
          </div>
          {has('report_generation') ? (
            <div className="flex gap-2">
              <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
                <SelectTrigger className="w-28 font-body text-sm" data-testid="district-select"><SelectValue/></SelectTrigger>
                <SelectContent>{DISTRICTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="gen-postcode" onClick={genPostcode} disabled={!!generating} className="flex-1 bg-info-blue text-white hover:bg-info-blue/90 font-body text-sm rounded-[4px]">
                {generating==='postcode' ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <FileText className="w-4 h-4 mr-2"/>}
                {generating==='postcode' ? 'Generating…' : `Generate ${selectedDistrict} Deep-Dive`}
              </Button>
            </div>
          ) : (
            <Gated feature="report_generation" minPlan="pro" title="Postcode Deep-Dive is Pro" testId="gated-postcode-report">
              <></>
            </Gated>
          )}
        </div>
        <div className="solid-card p-5 border-l-4 border-l-status-green md:col-span-2" data-testid="report-business">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-[4px] bg-status-green/10 flex items-center justify-center shrink-0"><Search className="w-5 h-5 text-status-green" strokeWidth={1.5} /></div>
            <div>
              <h3 className="font-display text-base font-extrabold text-ink">Targeted Business List (Export)</h3>
              <p className="font-body text-xs text-ink/50 mt-0.5">Run a filtered search and export the results as a formatted PDF for distribution to delivery partners. <strong className="text-ink">Pro feature.</strong></p>
            </div>
          </div>
          {has('pdf_export') ? (
            <div className="flex gap-2">
              <input data-testid="biz-search-input" type="text" value={businessQuery} onChange={e=>setBusinessQuery(e.target.value)} placeholder="e.g. TS18, Construction, Smith…" className="flex-1 px-3 py-2 border border-ink/10 rounded-[4px] font-body text-sm focus:outline-none focus:border-brand-orange" onKeyDown={e=>e.key==='Enter'&&genBusiness()} />
              <Button data-testid="gen-business" onClick={genBusiness} disabled={!!generating||!businessQuery.trim()} className="bg-status-green text-white hover:bg-status-green/90 font-body text-sm rounded-[4px]">
                {generating==='business' ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <FileText className="w-4 h-4 mr-2"/>}
                {generating==='business' ? 'Generating…' : 'Generate PDF'}
              </Button>
            </div>
          ) : (
            <Gated feature="pdf_export" minPlan="pro" title="PDF export is Pro" testId="gated-business-report">
              <></>
            </Gated>
          )}
        </div>
      </div>
    </div>
  );
}
